import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  resolveRecipeStepChips,
  resolveStepIngredientChips,
} from '../../utils/resolveStepIngredients';
import { Paprika, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import type { RecipeIngredient, StepIngredientMap } from '../../types/recipe';
import type { IngredientGroupInput } from '../../utils/ingredientGrouping';
import type { AdjustOptions } from '../../utils/adjustRecipeQuantities';

const isWeb = Platform.OS === 'web';

interface Props {
  stepText: string;
  allIngredients: RecipeIngredient[];
  /** Desktop variant (handoff `StepIng` big): larger text and padding. */
  big?: boolean;
  /**
   * Servings ratio + measurement system. Without it the chips would show base
   * quantities while the rest of the screen shows adjusted ones.
   */
  options?: AdjustOptions;
  /**
   * Every step of the recipe, in order. Pass it whenever the caller has it:
   * matching against the whole recipe is what lets a vague mention be refused
   * on one step without the ingredient going missing from all of them.
   */
  recipeStepTexts?: string[];
  /** This step's position in `recipeStepTexts`. */
  stepIndex?: number;
  /** The step→ingredient map resolved at import, when the recipe carries one. */
  storedStepIngredients?: StepIngredientMap[];
  /** Ingredient sections, so "tous les ingrédients" scopes to one of them. */
  ingredientGroups?: IngredientGroupInput[];
}

/**
 * Per-step ingredient chips (handoff `StepIng`): white pill, warm hairline,
 * 6px terracotta dot + label. Overflowing pills wrap onto the next row —
 * never a horizontal scroll. Renders nothing when no ingredient matches.
 *
 * A quantity we rescaled or converted is marked, so a chip never quietly
 * disagrees with the ingredient list.
 */
export const StepIngChips: React.FC<Props> = ({
  stepText,
  allIngredients,
  big = false,
  options,
  recipeStepTexts,
  stepIndex,
  storedStepIngredients,
  ingredientGroups,
}) => {
  const { t } = useTranslation();
  const recipeChips = useMemo(
    () =>
      recipeStepTexts?.length
        ? resolveRecipeStepChips(
            recipeStepTexts,
            allIngredients,
            options,
            storedStepIngredients,
            ingredientGroups,
          )
        : null,
    [recipeStepTexts, allIngredients, options, storedStepIngredients, ingredientGroups]
  );
  const chips = useMemo(
    () =>
      (stepIndex != null ? recipeChips?.[stepIndex] : undefined) ??
      resolveStepIngredientChips(stepText, allIngredients, options),
    [recipeChips, stepIndex, stepText, allIngredients, options]
  );
  if (!chips.length) return null;

  return (
    <View style={styles.wrap}>
      {chips.map((chip, i) => (
        <View key={i} style={[styles.chip, big && styles.chipBig]}>
          <View style={styles.dot} />
          <Text style={[styles.chipText, big && styles.chipTextBig]}>
            {chip.quantity ? (
              <Text
                style={chip.changed ? styles.changed : undefined}
                {...(isWeb && chip.changed
                  ? { title: t('recipeDetail.originallyValue', {
                        defaultValue: "à l'origine : {{value}}",
                        value: chip.original,
                      }) }
                  : {})}
              >
                {chip.quantity}
              </Text>
            ) : null}
            {chip.quantity ? ' · ' : ''}
            {chip.name}
          </Text>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 9,
    marginTop: 16,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.hairline,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 7,
    paddingHorizontal: 12,
    ...PaprikaShadows.card,
  },
  chipBig: {
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Paprika.accent,
  },
  chipText: {
    fontSize: 12.5,
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    color: Paprika.textSecondary,
  },
  chipTextBig: {
    fontSize: 14,
  },
  changed: {
    color: Paprika.accent,
    ...(isWeb
      ? { textDecorationLine: 'underline', textDecorationStyle: 'dotted' as const }
      : {}),
  },
});
